type TenantMapsLibrary = 'places' | 'maps';

class TenantGoogleMapsLoader {
  private scriptPromise: Promise<void> | null = null;
  private readonly libraryPromises = new Map<TenantMapsLibrary, Promise<void>>();

  /** Load Maps JS bootstrap + optional libraries (deduped across the tenant app). */
  prefetch(libraries: TenantMapsLibrary[] = ['places', 'maps']): Promise<void[]> {
    return Promise.all(libraries.map((lib) => this.importLibrary(lib)));
  }

  async importLibrary(library: TenantMapsLibrary): Promise<void> {
    const existing = this.libraryPromises.get(library);
    if (existing) return existing;

    const promise = (async () => {
      await this.ensureScript();
      await google.maps.importLibrary(library);
    })();

    this.libraryPromises.set(library, promise);
    return promise;
  }

  async isReady(library: TenantMapsLibrary): Promise<boolean> {
    try {
      await this.importLibrary(library);
      return true;
    } catch {
      return false;
    }
  }

  private async ensureScript(): Promise<void> {
    if (window.google?.maps?.importLibrary) {
      return;
    }

    if (this.scriptPromise) {
      await this.scriptPromise;
      return;
    }

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      throw new Error('Google Maps API key is not configured');
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[src*="maps.googleapis.com"]');
      if (existing) {
        const started = Date.now();
        const timer = window.setInterval(() => {
          if (window.google?.maps?.importLibrary) {
            window.clearInterval(timer);
            resolve();
          } else if (Date.now() - started > 20000) {
            window.clearInterval(timer);
            reject(new Error('Google Maps script timed out'));
          }
        }, 50);
        return;
      }

      const script = document.createElement('script');
      script.id = 'tenant-google-maps-api';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&v=weekly`;
      script.async = true;
      script.onload = () => {
        const started = Date.now();
        const timer = window.setInterval(() => {
          if (window.google?.maps?.importLibrary) {
            window.clearInterval(timer);
            resolve();
          } else if (Date.now() - started > 20000) {
            window.clearInterval(timer);
            reject(new Error('Google Maps bootstrap timed out'));
          }
        }, 50);
      };
      script.onerror = () => reject(new Error('Failed to load Google Maps script'));
      document.head.appendChild(script);
    });

    try {
      await this.scriptPromise;
    } catch (error) {
      this.scriptPromise = null;
      throw error;
    }
  }
}

export const tenantGoogleMapsLoader = new TenantGoogleMapsLoader();
